import { useState, useEffect } from 'react';
import './App.css';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import Dashboard from './components/pages/Dashboard';
import Contacts from './components/pages/Contacts';
import Companies from './components/pages/Companies';
import Opportunities from './components/pages/Opportunities';
import Quotes from './components/pages/Quotes';
import QuoteAssociationGaps from './components/pages/QuoteAssociationGaps';
import FieldMappingsConfig from './components/pages/FieldMappingsConfig';
import ValueMappings from './components/pages/ValueMappings';
import LookupMappings from './components/pages/LookupMappings';
import CompanyIdMappings from './components/pages/CompanyIdMappings';
import UnmappedCompanies from './components/pages/UnmappedCompanies';
import Placeholder from './components/pages/Placeholder';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Use runtime config (from env.sh) with fallback to build-time config
const API_URL = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL

// Parse the current location once at module load — used to deep-link from
// emails like /unmapped-companies?autotask_id=X&action=create.
const PATH_TO_PAGE = {
  '/unmapped-companies': 'unmapped-companies',
  '/lookup-mappings': 'lookup-mappings',
  '/contacts': 'contacts',
  '/companies': 'companies',
  '/opportunities': 'opportunities',
  '/quotes': 'quotes',
  '/quote-association-gaps': 'quote-association-gaps',
  '/dashboard': 'dashboard',
};
function parseInitialRoute() {
  const path = window.location.pathname.replace(/\/$/, '');
  const page = PATH_TO_PAGE[path] || 'contacts';
  const sp = new URLSearchParams(window.location.search);
  const params = {};
  for (const [k, v] of sp.entries()) params[k] = v;
  return { page, params };
}

function App() {
  const initialRoute = parseInitialRoute();
  const [activePage, setActivePage] = useState(initialRoute.page);
  const [urlParams, setUrlParams] = useState(initialRoute.params);
  const [loading, setLoading] = useState(false);
  const [environment, setEnvironment] = useState('production');

  // When the user navigates via the sidebar, clear the URL params (they were one-shot).
  const navigateTo = (page) => {
    setActivePage(page);
    setUrlParams({});
    // The association audit intentionally reads and updates production mappings.
    // Never carry a previously selected sandbox context onto this page.
    if (page === 'quote-association-gaps') setEnvironment('production');
    // Keep the address bar in sync so refresh stays on the page.
    const targetPath = Object.entries(PATH_TO_PAGE).find(([, p]) => p === page)?.[0] || '/';
    if (window.location.pathname !== targetPath || window.location.search) {
      window.history.replaceState(null, '', targetPath);
    }
  };

  // Helper function for API calls with environment header and credentials
  const apiFetch = async (endpoint, options = {}) => {
    const headers = {
      'X-Environment': environment,
      ...options.headers
    };
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await fetch(`${API_URL}${endpoint}`, { 
      ...options, 
      headers,
      credentials: 'include' // Important: send cookies with every request
    });
    if (response.status === 403) {
      window.location.replace('/?error=unauthorized');
    }
    return response;
  };

  // Handle environment change - refetch current page data
  const handleEnvironmentChange = (newEnv) => {
    setEnvironment(newEnv);
    // Data will be refetched by the useEffect due to environment dependency
  };

  // Data states
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    synced: 0,
    pending: 0,
    errors: 0
  });
  const [opportunityStats, setOpportunityStats] = useState({
    total: 0,
    synced: 0,
    pending: 0,
    errors: 0,
    totalValue: 0,
    averageValue: 0,
    winRate: 0,
    syncCoverage: 0,
    highValueCount: 0
  });
  const [quotes, setQuotes] = useState([]);
  const [quoteStats, setQuoteStats] = useState({
    total: 0,
    synced: 0,
    pending: 0,
    errors: 0,
    totalValue: 0,
    averageValue: 0,
    hubspotCoverage: 0,
    syncCoverage: 0,
    expiringSoon: 0,
    expired: 0
  });

  // Pagination states
  const [contactsPagination, setContactsPagination] = useState(null);
  const [companiesPagination, setCompaniesPagination] = useState(null);
  const [opportunitiesPagination, setOpportunitiesPagination] = useState(null);
  const [quotesPagination, setQuotesPagination] = useState(null);

  // Sync configuration states
  const [fieldMappings, setFieldMappings] = useState([]);
  const [defaultValues, setDefaultValues] = useState([]);
  const [valueMappings, setValueMappings] = useState([]);  // ✅ NEW STATE
  const [lookupMappings, setLookupMappings] = useState([]);
  const [companyIdMappings, setCompanyIdMappings] = useState([]);

  // Entity type filter (shared between Field Mappings, Default Values, and Value Mappings)
  const [selectedEntityType, setSelectedEntityType] = useState('Company');

  // ============================================
  // DATA FETCHING - Based on active page
  // ============================================
  useEffect(() => {
    switch (activePage) {
      case 'contacts':
        fetchContacts();
        break;
      case 'companies':
        fetchCompanies();
        break;
      case 'opportunities':
        fetchOpportunities();
        break;
      case 'quotes':
        fetchQuotes();
        break;
      case 'quote-association-gaps':
        // Data is fetched once by the page so it can enforce its no-polling policy.
        break;
      case 'field-config':
        fetchFieldMappings();
        fetchDefaultValues();
        break;
      case 'value-mappings':        // ✅ NEW CASE
        fetchValueMappings();
        break;
      case 'lookup-mappings':
        fetchLookupMappings();
        break;
      case 'company-id-mappings':
        fetchCompanyIdMappings();
        break;
      case 'unmapped-companies':
        // Data fetched internally by the component
        break;
      default:
        break;
    }
  }, [activePage, selectedEntityType, environment]);

  // ============================================
  // CONTACTS & COMPANIES
  // ============================================
  const fetchContacts = async (page = 1, perPage = 50, sortBy = 'last_sync_at', sortDir = 'desc', search = '') => {
    setLoading(true);
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await apiFetch(`/contacts?page=${page}&per_page=${perPage}&sort_by=${sortBy}&sort_dir=${sortDir}${searchParam}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setContacts(result.contacts || []);
      setStats(result.stats || { errors: 0, pending: 0, synced: 0, total: 0 });
      setContactsPagination(result.pagination || null);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      alert('Error loading contacts: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async (page = 1, perPage = 50, search = '') => {
    setLoading(true);
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await apiFetch(`/companies?page=${page}&per_page=${perPage}${searchParam}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setCompanies(result.companies || []);
      setStats(result.stats || { errors: 0, pending: 0, synced: 0, total: 0 });
      setCompaniesPagination(result.pagination || null);
    } catch (error) {
      console.error('Error fetching companies:', error);
      alert('Error loading companies: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOpportunities = async (page = 1, perPage = 50, search = '') => {
    setLoading(true);
    try {
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
      const response = await apiFetch(`/opportunities?page=${page}&per_page=${perPage}${searchParam}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setOpportunities(result.opportunities || []);
      setOpportunityStats(result.stats || {
        total: 0,
        synced: 0,
        pending: 0,
        errors: 0,
        totalValue: 0,
        averageValue: 0,
        winRate: 0,
        syncCoverage: 0,
        highValueCount: 0
      });
      setOpportunitiesPagination(result.pagination || null);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      alert('Error loading opportunities: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async (page = 1, perPage = 50) => {
    setLoading(true);
    try {
      const response = await apiFetch(`/quotes?page=${page}&per_page=${perPage}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setQuotes(result.quotes || []);
      setQuoteStats(result.stats || {
        total: 0,
        synced: 0,
        pending: 0,
        errors: 0,
        totalValue: 0,
        averageValue: 0,
        hubspotCoverage: 0,
        syncCoverage: 0,
        expiringSoon: 0,
        expired: 0
      });
      setQuotesPagination(result.pagination || null);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      alert('Error loading quotes: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // FIELD MAPPINGS
  // ============================================
  const fetchFieldMappings = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/field-mappings?entity_type=${selectedEntityType}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setFieldMappings(result.mappings || []);
    } catch (error) {
      console.error('Error fetching field mappings:', error);
      alert('Error loading field mappings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFieldMapping = async (formData, editId) => {
    try {
      const endpoint = editId ? `/field-mappings/${editId}` : `/field-mappings`;
      const method = editId ? 'PUT' : 'POST';

      const response = await apiFetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      alert(editId ? 'Field mapping updated!' : 'Field mapping created!');
      fetchFieldMappings();
      return true;
    } catch (error) {
      console.error('Error saving field mapping:', error);
      alert('Error saving field mapping: ' + error.message);
      return false;
    }
  };

  const handleDeleteFieldMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this field mapping?')) return;
    try {
      const response = await apiFetch(`/field-mappings/${mappingId}`, { method: 'DELETE' });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      alert('Field mapping deleted!');
      fetchFieldMappings();
    } catch (error) {
      console.error('Error deleting field mapping:', error);
      alert('Error deleting field mapping: ' + error.message);
    }
  };

  // ============================================
  // DEFAULT VALUES
  // ============================================
  const fetchDefaultValues = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/default-values?entity_type=${selectedEntityType}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setDefaultValues(result.defaults || []);
    } catch (error) {
      console.error('Error fetching default values:', error);
      alert('Error loading default values: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDefaultValue = async (formData, editId) => {
    try {
      const endpoint = editId ? `/default-values/${editId}` : `/default-values`;
      const method = editId ? 'PUT' : 'POST';

      const response = await apiFetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      alert(editId ? 'Default value updated!' : 'Default value created!');
      fetchDefaultValues();
      return true;
    } catch (error) {
      console.error('Error saving default value:', error);
      alert('Error saving default value: ' + error.message);
      return false;
    }
  };

  const handleDeleteDefaultValue = async (defaultId) => {
    if (!window.confirm('Are you sure you want to delete this default value?')) return;
    try {
      const response = await apiFetch(`/default-values/${defaultId}`, { method: 'DELETE' });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      alert('Default value deleted!');
      fetchDefaultValues();
    } catch (error) {
      console.error('Error deleting default value:', error);
      alert('Error deleting default value: ' + error.message);
    }
  };

  // ============================================
  // VALUE MAPPINGS ✅ NEW SECTION
  // ============================================
  const fetchValueMappings = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/value-mappings?entity_type=${selectedEntityType}`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setValueMappings(result.mappings || []);
    } catch (error) {
      console.error('Error fetching value mappings:', error);
      alert('Error loading value mappings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveValueMapping = async (formData, editId) => {
    try {
      const endpoint = editId ? `/value-mappings/${editId}` : `/value-mappings`;
      const method = editId ? 'PUT' : 'POST';

      const response = await apiFetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      alert(editId ? 'Value mapping updated!' : 'Value mapping created!');
      fetchValueMappings();
      return true;
    } catch (error) {
      console.error('Error saving value mapping:', error);
      alert('Error saving value mapping: ' + error.message);
      return false;
    }
  };

  const handleDeleteValueMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this value mapping?')) return;
    try {
      const response = await apiFetch(`/value-mappings/${mappingId}`, { method: 'DELETE' });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      alert('Value mapping deleted!');
      fetchValueMappings();
    } catch (error) {
      console.error('Error deleting value mapping:', error);
      alert('Error deleting value mapping: ' + error.message);
    }
  };

  // ============================================
  // LOOKUP MAPPINGS
  // ============================================
  const fetchLookupMappings = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/lookup-mappings`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setLookupMappings(result.mappings || []);
    } catch (error) {
      console.error('Error fetching lookup mappings:', error);
      alert('Error loading lookup mappings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLookupMapping = async (formData, editId) => {
    try {
      const endpoint = editId ? `/lookup-mappings/${editId}` : `/lookup-mappings`;
      const method = editId ? 'PUT' : 'POST';

      const response = await apiFetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      alert(editId ? 'Lookup mapping updated!' : 'Lookup mapping created!');
      fetchLookupMappings();
      return true;
    } catch (error) {
      console.error('Error saving lookup mapping:', error);
      alert('Error saving lookup mapping: ' + error.message);
      return false;
    }
  };

  const handleDeleteLookupMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this lookup mapping?')) return;
    try {
      const response = await apiFetch(`/lookup-mappings/${mappingId}`, { method: 'DELETE' });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      alert('Lookup mapping deleted!');
      fetchLookupMappings();
    } catch (error) {
      console.error('Error deleting lookup mapping:', error);
      alert('Error deleting lookup mapping: ' + error.message);
    }
  };

  // ============================================
  // COMPANY ID MAPPINGS
  // ============================================
  const fetchCompanyIdMappings = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`/company-id-mappings`);
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      setCompanyIdMappings(result.mappings || []);
    } catch (error) {
      console.error('Error fetching company ID mappings:', error);
      alert('Error loading company ID mappings: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanyIdMapping = async (formData, editId) => {
    try {
      const endpoint = editId ? `/company-id-mappings/${editId}` : `/company-id-mappings`;
      const method = editId ? 'PUT' : 'POST';

      const response = await apiFetch(endpoint, {
        method,
        body: JSON.stringify(formData)
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);

      alert(editId ? 'Company ID mapping updated!' : 'Company ID mapping created!');
      fetchCompanyIdMappings();
      return true;
    } catch (error) {
      console.error('Error saving company ID mapping:', error);
      alert('Error saving company ID mapping: ' + error.message);
      return false;
    }
  };

  const handleDeleteCompanyIdMapping = async (mappingId) => {
    if (!window.confirm('Are you sure you want to delete this company ID mapping?')) return;
    try {
      const response = await apiFetch(`/company-id-mappings/${mappingId}`, { method: 'DELETE' });
      const result = await response.json();
      if (!result.success) throw new Error(result.error);
      alert('Company ID mapping deleted!');
      fetchCompanyIdMappings();
    } catch (error) {
      console.error('Error deleting company ID mapping:', error);
      alert('Error deleting company ID mapping: ' + error.message);
    }
  };

  // ============================================
  // PAGE ROUTING
  // ============================================
  const renderPageContent = () => {
    switch (activePage) {
      case 'dashboard':
        return <Dashboard />;
      
      case 'contacts':
        return (
          <Contacts
            contacts={contacts}
            stats={stats}
            loading={loading}
            pagination={contactsPagination}
            fetchContacts={fetchContacts}
            apiFetch={apiFetch}
          />
        );

      case 'companies':
        return (
          <Companies
            companies={companies}
            stats={stats}
            loading={loading}
            pagination={companiesPagination}
            fetchCompanies={fetchCompanies}
            apiFetch={apiFetch}
          />
        );
      
      // ========== SYNC CONFIGURATION ==========
      case 'field-config':
        return (
          <FieldMappingsConfig
            fieldMappings={fieldMappings}
            defaultValues={defaultValues}
            loading={loading}
            selectedEntityType={selectedEntityType}
            setSelectedEntityType={setSelectedEntityType}
            fetchFieldMappings={fetchFieldMappings}
            fetchDefaultValues={fetchDefaultValues}
          />
        );
      
      case 'value-mappings':
        return (
          <ValueMappings
            valueMappings={valueMappings}
            loading={loading}
            selectedEntityType={selectedEntityType}
            setSelectedEntityType={setSelectedEntityType}
            fetchValueMappings={fetchValueMappings}
          />
        );
      
      case 'lookup-mappings':
        return (
          <LookupMappings
            lookupMappings={lookupMappings}
            loading={loading}
            fetchLookupMappings={fetchLookupMappings}
            handleSaveLookupMapping={handleSaveLookupMapping}
            handleDeleteLookupMapping={handleDeleteLookupMapping}
            apiFetch={apiFetch}
          />
        );
      
      case 'company-id-mappings':
        return (
          <CompanyIdMappings
            companyIdMappings={companyIdMappings}
            loading={loading}
            fetchCompanyIdMappings={fetchCompanyIdMappings}
            handleSaveCompanyIdMapping={handleSaveCompanyIdMapping}
            handleDeleteCompanyIdMapping={handleDeleteCompanyIdMapping}
          />
        );
      
      case 'unmapped-companies':
        return (
          <UnmappedCompanies
            apiFetch={apiFetch}
            loading={loading}
            urlParams={urlParams}
          />
        );

      case 'sync-config':
        return <Placeholder pageName="Sync Overview" />;
      // ========== OTHER PAGES ==========
      case 'executions':
        return <Placeholder pageName="Executions" />;
      case 'sync':
        return <Placeholder pageName="Sync Status" />;
      case 'opportunities':
        return (
          <Opportunities
            opportunities={opportunities}
            stats={opportunityStats}
            loading={loading}
            pagination={opportunitiesPagination}
            fetchOpportunities={fetchOpportunities}
            apiFetch={apiFetch}
          />
        );
      case 'quotes':
        return (
          <Quotes
            quotes={quotes}
            stats={quoteStats}
            loading={loading}
            pagination={quotesPagination}
            fetchQuotes={fetchQuotes}
            apiFetch={apiFetch}
          />
        );
      case 'quote-association-gaps':
        return (
          <QuoteAssociationGaps
            apiFetch={apiFetch}
            environment={environment}
          />
        );
      case 'errors':
        return <Placeholder pageName="Errors" />;
      case 'analytics':
        return <Placeholder pageName="Analytics" />;
      case 'settings':
        return <Placeholder pageName="Settings" />;
      
      default:
        console.warn(`Unknown page: ${activePage}`);
        return <Dashboard />;
    }
  };

  return (
    <AuthProvider>
      <ProtectedRoute>
        <div className="container">
          <Sidebar activePage={activePage} setActivePage={navigateTo} />
          
          <main className="main-content">
            <Header activePage={activePage} environment={environment} onEnvironmentChange={handleEnvironmentChange} />
            {renderPageContent()}
          </main>
        </div>
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;
